import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, APIError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { StatusBadge, type Status } from '../components/StatusBadge'
import { CommentThread } from '../components/CommentThread'
import { AIScoreBadge } from '../components/AIScoreBadge'
import { ProcessBadge } from '../components/ProcessBadge'

interface CommentAuthor { id: string; full_name: string }
interface Comment { id: string; text: string; author: CommentAuthor; created_at: string }
interface StatusHistoryEntry {
  from_status: Status | null
  to_status: Status
  changed_by_user_id: string | null
  comment: string | null
  changed_at: string
}
interface AIAssessment {
  model_name: string
  prompt_version: string
  viability_score: number
  ai_score: number
  pros: string[]
  cons: string[]
  risks: Array<{ description: string; severity: string }>
  effort: { hours_low: number; hours_high: number; complexity: string }
  cost: { rub_low: number; rub_high: number }
  reasoning: string
  flagged_for_review: boolean
  flag_reason: string | null
  updated_at: string
}
interface IdeaAuthor {
  id: string
  full_name: string
  employee_number?: string | null
  position?: string | null
  department_name?: string | null
  business_unit_name?: string | null
}
interface IdeaDetail {
  id: string
  code: string
  title: string
  description: string
  description_normalized: string | null
  status: Status
  ai_status: string
  ai_error: string | null
  author: IdeaAuthor | null
  target_department_id: string | null
  target_department_name: string | null
  assigned_manager_id: string | null
  implementation_plan: string | null
  implementation_fact: string | null
  source: string
  version: number
  process_tags?: string[]
  comments: Comment[]
  status_history: StatusHistoryEntry[]
  ai_assessment: AIAssessment | null
  created_at: string
  updated_at: string
}

export const Route = createFileRoute('/ideas/$id')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    if (!useAuth.getState().user) {
      throw redirect({ to: '/login' })
    }
  },
  component: IdeaDetailPage,
})

function IdeaDetailPage() {
  const { id } = Route.useParams()
  const user = useAuth((s) => s.user)
  const { data: idea, isLoading, error } = useQuery({
    queryKey: ['idea', id],
    queryFn: () => api<IdeaDetail>(`/api/ideas/${id}`),
    refetchInterval: (query) => {
      const status = query.state.data?.ai_status
      return status === 'done' || status === 'failed' ? false : 3000
    },
  })

  if (isLoading) {
    return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  }
  if (error || !idea) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <p className="text-sm text-red-600">Идея не найдена</p>
        <Link to="/my-ideas" className="text-sm text-blue-600 hover:underline">
          ← К моим идеям
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6">
      <div>
        <Link to="/my-ideas" className="text-sm text-blue-600 hover:underline">
          ← К моим идеям
        </Link>
      </div>

      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-mono text-zinc-500">{idea.code}</p>
            <h1 className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-100">
              {idea.title}
            </h1>
            {idea.process_tags && idea.process_tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {idea.process_tags.map((code) => (
                  <ProcessBadge key={code} code={code} size="sm" />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={idea.status} />
            {idea.ai_assessment && <AIScoreBadge score={idea.ai_assessment.ai_score} />}
          </div>
        </div>
        {(idea.author || idea.target_department_name) && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {idea.author && (
              <div className="bg-zinc-50 dark:bg-zinc-900 rounded-md p-3">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  👤 Автор
                </p>
                <p className="text-zinc-700 dark:text-zinc-300">
                  {idea.author.full_name}
                </p>
                {idea.author.position && (
                  <p className="text-zinc-500 mt-0.5">{idea.author.position}</p>
                )}
                {idea.author.employee_number && (
                  <p className="text-zinc-500 mt-0.5">
                    ТН: <span className="font-mono">{idea.author.employee_number}</span>
                  </p>
                )}
                {(idea.author.business_unit_name || idea.author.department_name) && (
                  <p className="text-zinc-500 mt-0.5">
                    {[idea.author.business_unit_name, idea.author.department_name]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
            )}
            {idea.target_department_name && (
              <div className="bg-zinc-50 dark:bg-zinc-900 rounded-md p-3">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  🏭 Подразделение идеи
                </p>
                <p className="text-zinc-700 dark:text-zinc-300">
                  {idea.target_department_name}
                </p>
              </div>
            )}
          </div>
        )}
        <p className="mt-4 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
          {idea.description}
        </p>
        {idea.ai_status === 'pending' && (
          <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            🤖 AI обрабатывает идею…
          </p>
        )}
        {idea.ai_status === 'running' && (
          <p className="mt-3 text-xs text-blue-600 dark:text-blue-400 inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            🤖 AI работает…
          </p>
        )}
        {idea.ai_status === 'failed' && (
          <p className="mt-3 text-xs text-red-600">
            🤖 AI оценка не удалась: {idea.ai_error || 'неизвестная ошибка'}
          </p>
        )}
        {idea.implementation_plan && (
          <div className="mt-4 text-sm">
            <p className="font-semibold text-zinc-900 dark:text-zinc-100">План реализации</p>
            <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {idea.implementation_plan}
            </p>
          </div>
        )}
        {idea.implementation_fact && (
          <div className="mt-4 text-sm">
            <p className="font-semibold text-zinc-900 dark:text-zinc-100">Факт реализации</p>
            <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {idea.implementation_fact}
            </p>
          </div>
        )}
      </section>

      {idea.ai_assessment && (
        <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              🤖 AI-оценка
            </h2>
            <span className="text-xs text-zinc-500">
              Вменяемость: <strong>{idea.ai_assessment.viability_score}/100</strong>
            </span>
          </div>

          {idea.ai_assessment.flagged_for_review && (
            <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded text-xs text-amber-900 dark:text-amber-100">
              ⚠️ Оценка под вопросом: {idea.ai_assessment.flag_reason}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
            <div>
              <h3 className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">✓ Плюсы</h3>
              <ul className="space-y-1 list-disc list-inside text-zinc-700 dark:text-zinc-300">
                {idea.ai_assessment.pros.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">✗ Минусы</h3>
              <ul className="space-y-1 list-disc list-inside text-zinc-700 dark:text-zinc-300">
                {idea.ai_assessment.cons.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1">⚠️ Риски</h3>
              <ul className="space-y-1 text-zinc-700 dark:text-zinc-300">
                {idea.ai_assessment.risks.map((r, i) => (
                  <li key={i}>
                    [<span className={
                      r.severity === 'high' ? 'text-red-600' :
                      r.severity === 'medium' ? 'text-amber-600' :
                      'text-zinc-500'
                    }>{r.severity}</span>] {r.description}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
            <div className="bg-zinc-50 dark:bg-zinc-900 p-3 rounded-md">
              📈 <strong>Трудозатраты:</strong> {idea.ai_assessment.effort.hours_low}–{idea.ai_assessment.effort.hours_high} ч
              <span className="ml-2 text-xs text-zinc-500">({idea.ai_assessment.effort.complexity})</span>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900 p-3 rounded-md">
              💰 <strong>Стоимость:</strong> {idea.ai_assessment.cost.rub_low.toLocaleString('ru-RU')}–{idea.ai_assessment.cost.rub_high.toLocaleString('ru-RU')} ₽
            </div>
          </div>

          <details className="mt-3 text-xs text-zinc-600 dark:text-zinc-400">
            <summary className="cursor-pointer hover:text-zinc-900 dark:hover:text-zinc-100">
              💭 Обоснование AI
            </summary>
            <p className="mt-2 whitespace-pre-wrap">{idea.ai_assessment.reasoning}</p>
          </details>

          <p className="mt-3 text-xs text-zinc-400">
            Модель: {idea.ai_assessment.model_name} · обновлено: {new Date(idea.ai_assessment.updated_at).toLocaleString('ru-RU')}
          </p>
        </section>
      )}

      {user && user.role !== 'user' && <ManagerControls idea={idea} />}

      <section>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
          История статусов
        </h2>
        <ol className="space-y-1 text-sm">
          {idea.status_history.map((h, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-xs text-zinc-500 font-mono">
                {new Date(h.changed_at).toLocaleString('ru-RU')}
              </span>
              {h.from_status ? (
                <>
                  <StatusBadge status={h.from_status} />
                  <span className="text-zinc-400">→</span>
                </>
              ) : (
                <span className="text-zinc-400">подано →</span>
              )}
              <StatusBadge status={h.to_status} />
              {h.comment && (
                <span className="text-xs text-zinc-600 dark:text-zinc-400">— {h.comment}</span>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
          Обсуждение
        </h2>
        <CommentThread ideaId={idea.id} comments={idea.comments} />
      </section>
    </div>
  )
}

function ManagerControls({ idea }: { idea: IdeaDetail }) {
  const qc = useQueryClient()
  const [statusId, setStatusId] = useState(idea.status.id)
  const [assignee, setAssignee] = useState(idea.assigned_manager_id ?? '')
  const [plan, setPlan] = useState(idea.implementation_plan ?? '')
  const [fact, setFact] = useState(idea.implementation_fact ?? '')
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: statuses } = useQuery({
    queryKey: ['lookups', 'statuses'],
    queryFn: () => api<Status[]>('/api/lookups/statuses'),
  })

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { version: idea.version }
      if (statusId !== idea.status.id) {
        body.status_id = statusId
        if (comment.trim()) body.status_change_comment = comment.trim()
      }
      if (assignee !== (idea.assigned_manager_id ?? '')) {
        body.assigned_manager_id = assignee || null
      }
      if (plan !== (idea.implementation_plan ?? '')) {
        body.implementation_plan = plan
      }
      if (fact !== (idea.implementation_fact ?? '')) {
        body.implementation_fact = fact
      }
      return api(`/api/ideas/${idea.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    },
    onSuccess: () => {
      setError(null)
      setComment('')
      void qc.invalidateQueries({ queryKey: ['idea', idea.id] })
    },
    onError: (err: unknown) => {
      if (err instanceof APIError) {
        if (err.status === 409) setError('Идея была изменена другим пользователем. Обнови страницу.')
        else if (err.status === 403) setError('Нет прав на редактирование')
        else if (err.status === 404) setError('Идея вне твоего scope')
        else setError(`Ошибка ${err.status}`)
      } else {
        setError('Не удалось сохранить изменения')
      }
    },
  })

  const hasChanges =
    statusId !== idea.status.id ||
    assignee !== (idea.assigned_manager_id ?? '') ||
    plan !== (idea.implementation_plan ?? '') ||
    fact !== (idea.implementation_fact ?? '')

  return (
    <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
        Управление (для менеджера)
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
            Статус
          </label>
          <select
            value={statusId}
            onChange={(e) => setStatusId(e.target.value)}
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-sm"
          >
            {statuses?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
            Ответственный (UUID)
          </label>
          <input
            type="text"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            placeholder="UUID пользователя или пусто"
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-xs font-mono"
          />
        </div>
        {statusId !== idea.status.id && (
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Комментарий к смене статуса (опц.)
            </label>
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-sm"
            />
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
            План реализации
          </label>
          <textarea
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
            Факт реализации
          </label>
          <textarea
            value={fact}
            onChange={(e) => setFact(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-sm"
          />
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={() => mutation.mutate()}
          disabled={!hasChanges || mutation.isPending}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
        >
          {mutation.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </section>
  )
}
